import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { BottomSheet } from '@/components/ui/BottomSheet';

const DISMISSED_KEY = 'vado_install_prompt_dismissed';

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function InstallPrompt() {
  const { t } = useTranslation();
  const { isStandalone, isIOS, isInstallable, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(isDismissed);
  const [showIosSheet, setShowIosSheet] = useState(false);

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // ignore — non-critical
    }
  };

  if (isStandalone || dismissed || (!isIOS && !isInstallable)) return null;

  return (
    <>
      <div
        className="fixed inset-x-0 top-0 z-40 px-3 pt-3 motion-safe:animate-slide-up"
        role="region"
        aria-label={t('install_prompt.banner_text')}
      >
        <Card className="flex items-center justify-between gap-3">
          <p className="m-0 text-sm text-gray-900">{t('install_prompt.banner_text')}</p>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => (isIOS ? setShowIosSheet(true) : promptInstall())}
            >
              {isIOS ? t('install_prompt.ios_install_btn') : t('install_prompt.install_btn')}
            </Button>
            <button
              type="button"
              onClick={dismiss}
              aria-label={t('install_prompt.dismiss')}
              className="text-lg leading-none text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
        </Card>
      </div>

      <BottomSheet
        open={showIosSheet}
        onClose={() => setShowIosSheet(false)}
        title={t('install_prompt.ios_sheet_title')}
      >
        <ol className="m-0 list-decimal space-y-3 pl-5 text-sm text-gray-700">
          <li>{t('install_prompt.ios_step_1')}</li>
          <li>{t('install_prompt.ios_step_2')}</li>
          <li>{t('install_prompt.ios_step_3')}</li>
        </ol>
        <Button
          variant="primary"
          className="mt-4 w-full"
          onClick={() => {
            setShowIosSheet(false);
            dismiss();
          }}
        >
          {t('install_prompt.ios_got_it')}
        </Button>
      </BottomSheet>
    </>
  );
}
